// R-216 Task C1 — collapsible per-step wrapper. Replaces QueueCard as the
// per-step container on the Queue Dashboard. Renders a header (step name + step
// doc total) over a dense lane table. Collapses on header click and persists
// the collapsed step set in localStorage (`capture.queue.collapsed`). Always
// renders if the step has lanes OR an active block (R-218) — an empty-but-blocked
// step still shows its header and the locked empty lane.

import { useCallback, useState } from "react";
import { Link } from "react-router";
import { Collapsible } from "@/components/Collapsible";
import { UNCLASSIFIED_JOB_TYPE_ID } from "../hooks/useQueues";
import type { JobTypeOperator, JobTypeQueueRow, LaneInstruction, QueueSummary } from "../hooks/useQueues";
import type { FeedLock } from "../hooks/useFeedLock";
import { formatEvictionNotice, type MyEviction } from "../../queue-evictions/api/client";
import { timeAgo } from "../lib/timeAgo";
import { JobTypeRow } from "./JobTypeRow";

const COLLAPSED_LS_KEY = "capture.queue.collapsed";

function readCollapsedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_LS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? new Set(arr.filter((s): s is string => typeof s === "string")) : new Set();
  } catch {
    return new Set();
  }
}

function persistCollapsed(stepType: string, collapsed: boolean): void {
  try {
    const set = readCollapsedSet();
    if (collapsed) set.add(stepType);
    else set.delete(stepType);
    localStorage.setItem(COLLAPSED_LS_KEY, JSON.stringify([...set]));
  } catch {
    /* localStorage unavailable — collapse state is best-effort */
  }
}

interface StepSectionProps {
  queue: QueueSummary;
  /** Currently-active feed lock (across all steps); highlights the matching lane. */
  activeFeedLock?: FeedLock | null;
  /** Fired when an operator starts feeding a lane: (stepType, jobTypeId). */
  onStartFeed?: (stepType: string, jobTypeId: string) => void;
  /** C3 — job-name / batches / docs click → Batches filtered by (step, jobType). */
  onNavigateToBatches?: (stepType: string, jobTypeId: string) => void;
  /** E-057 — failed-badge click → Export History filtered to the job type's failures. */
  onShowExportFailures?: (jobTypeId: string) => void;
  /** C3/C4 — supervisor lock-cell click → toggle the queue block. */
  onToggleLock?: (stepType: string, jobTypeId: string, currentlyBlocked: boolean) => void;
  /** C4 — viewer can toggle locks (supervisor.queue_block). */
  canToggleLock?: boolean;
  /** WU-2.2 — viewer holds supervisor.queue_instruct (or admin). */
  canInstruct?: boolean;
  /** WU-2.2 — open the instruction editor for (stepType, jobTypeId). */
  onEditInstruction?: (
    stepType: string,
    jobTypeId: string,
    current: LaneInstruction | null,
  ) => void;
  /**
   * WU-2.4 T7 — the viewing operator's own active evictions (from
   * `useMyEvictions`). A lane is evicted-locked when an eviction matches
   * `stepType === queue.stepType && (jobTypeId === null || jobTypeId ===
   * lane.jobTypeId)` — a step-wide eviction (jobTypeId: null) locks every
   * lane card in this step. Undefined/empty is fully inert (today's
   * rendering, byte-identical).
   */
  myEvictions?: MyEviction[];
}

export function StepSection({
  queue,
  activeFeedLock,
  onStartFeed,
  onNavigateToBatches,
  onShowExportFailures,
  onToggleLock,
  canToggleLock,
  canInstruct,
  onEditInstruction,
  myEvictions,
}: StepSectionProps) {
  const [collapsed, setCollapsed] = useState(() => readCollapsedSet().has(queue.stepType));
  // C3 — busy-cell click reveals the inline roster for that lane (monitor-gated:
  // a lane only carries `operators` when the viewer can monitor).
  const [openRoster, setOpenRoster] = useState<Set<string>>(() => new Set());

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      persistCollapsed(queue.stepType, next);
      return next;
    });
  }, [queue.stepType]);

  const toggleRoster = useCallback((jobTypeId: string) => {
    setOpenRoster((prev) => {
      const next = new Set(prev);
      if (next.has(jobTypeId)) next.delete(jobTypeId);
      else next.add(jobTypeId);
      return next;
    });
  }, []);

  // Step doc total = Σ lane docs (reconciles with the API documentsInProcess).
  const stepDocTotal = queue.byJobType.reduce((sum, lane) => sum + (lane.docs ?? lane.count ?? 0), 0);
  const isThisStepLocked = activeFeedLock?.stepType === queue.stepType;
  const hasLanes = queue.byJobType.length > 0;

  return (
    <Collapsible
      open={!collapsed}
      onToggle={toggle}
      className="qs-section"
      headerClassName="qs-header"
      bodyClassName="qs-body"
      chevronClassName="qs-chevron"
      header={
        <>
          <span className="qs-name">{formatStepType(queue.stepType)}</span>
          {(queue.awaitingRelease ?? 0) > 0 && (
            <span
              className="qs-awaiting-release"
              data-testid="awaiting-release-indicator"
              title="Held — awaiting supervisor release; not assignable"
            >
              <span className="qs-awaiting-release-count">{queue.awaitingRelease}</span>
              held — awaiting release
            </span>
          )}
          {queue.remittanceSummary ? (
            <span className="qs-total" data-testid="remittance-summary">
              {queue.remittanceSummary.batchCount} batches · {queue.remittanceSummary.transactionCount} transactions
            </span>
          ) : (
            <span className="qs-total">{stepDocTotal} docs</span>
          )}
        </>
      }
    >
          {queue.stepInstruction && (
            <div
              className="qs-step-instruction"
              data-testid="step-instruction"
              title={queue.stepInstruction.body}
            >
              <span aria-hidden="true">📌</span> {queue.stepInstruction.body}
            </div>
          )}
          {hasLanes ? (
            <div className="qc-jt-list">
              <div className="qc-jt-colhead" aria-hidden="true">
                <span className="qc-jt-colhead-name">Job Type</span>
                {/* E-051 — spacer over the per-lane claim column. */}
                <span className="qc-jt-claim-cell" />
                <span className="qc-jt-num">ready</span>
                <span className="qc-jt-num">busy</span>
                <span className="qc-jt-num">tot</span>
                <span className="qc-jt-num">batch</span>
                <span className="qc-jt-num">docs</span>
                <span className="qc-jt-lock-head" />
              </div>
              {queue.byJobType.map((lane) => {
                // WU-2.4 T7 — lane-scoped or step-wide (jobTypeId: null) active
                // eviction against the viewing operator. Precedence: an active
                // supervisor block (lane.blockedReason) wins over an eviction
                // notice on the same lane — mirrors the api's block-before-
                // eviction check order in POST /queues/next (queue-block gates
                // first, eviction gates second). The eviction copy only
                // renders when the lane isn't already blocked.
                const evicted = myEvictions?.find(
                  (e) =>
                    e.stepType === queue.stepType &&
                    (e.jobTypeId === null || e.jobTypeId === lane.jobTypeId),
                );
                const evictionMessage =
                  evicted && !lane.blockedReason
                    ? formatEvictionNotice(evicted.reason)
                    : undefined;
                return (
                <div key={lane.jobTypeId} className="qc-jt-entry">
                  <JobTypeRow
                    jobTypeId={lane.jobTypeId}
                    jobTypeName={lane.jobTypeName}
                    count={lane.count}
                    readyWi={lane.readyWi}
                    busyWi={lane.busyWi}
                    totWi={lane.totWi}
                    batches={lane.batches}
                    docs={lane.docs}
                    isActive={isThisStepLocked && activeFeedLock?.jobTypeId === lane.jobTypeId}
                    onStart={(jtId) => onStartFeed?.(queue.stepType, jtId)}
                    disabled={!onStartFeed}
                    blocked={Boolean(lane.blockedReason) || Boolean(evictionMessage)}
                    blockedReason={lane.blockedReason ?? evictionMessage}
                    exportFailed={lane.exportFailed}
                    onShowExportFailures={onShowExportFailures}
                    onNavigate={
                      onNavigateToBatches
                        ? (jtId) => onNavigateToBatches(queue.stepType, jtId)
                        : undefined
                    }
                    onToggleRoster={
                      lane.operators !== undefined ? toggleRoster : undefined
                    }
                    onToggleLock={
                      canToggleLock && onToggleLock
                        ? (jtId) =>
                            onToggleLock(queue.stepType, jtId, Boolean(lane.blockedReason))
                        : undefined
                    }
                    canToggleLock={canToggleLock}
                  />
                  {(() => {
                    // I-1 — the Unclassified sentinel lane (jobTypeId
                    // "_unclassified") is synthetic on the api side and has
                    // no row in the JobType table; PUT
                    // /api/admin/queue-instructions validates jobTypeId
                    // against that table, so a save on this lane always
                    // 404s. Suppress ONLY the edit affordance here — an
                    // existing instruction preview (set via the step-wide
                    // scope, which renders separately) must still render.
                    const canEditThisLane =
                      canInstruct &&
                      onEditInstruction &&
                      lane.jobTypeId !== UNCLASSIFIED_JOB_TYPE_ID;
                    if (!lane.instruction && !canEditThisLane) return null;
                    return (
                      <div className="qc-jt-instruction" data-testid="jt-instruction">
                        {lane.instruction && (
                          <span
                            className="qc-jt-instruction-body"
                            title={lane.instruction.body}
                          >
                            <span aria-hidden="true">📌</span> {lane.instruction.body}
                          </span>
                        )}
                        {canEditThisLane && (
                          <button
                            type="button"
                            className="qc-jt-instruction-edit"
                            onClick={() =>
                              onEditInstruction(
                                queue.stepType,
                                lane.jobTypeId,
                                lane.instruction ?? null,
                              )
                            }
                          >
                            {lane.instruction ? "Edit instruction" : "Set instruction"}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  {evictionMessage && (
                    <div className="qc-jt-eviction" data-testid="jt-eviction-notice">
                      {evictionMessage}
                    </div>
                  )}
                  {lane.operators !== undefined && openRoster.has(lane.jobTypeId) && (
                    <OperatorRoster row={lane} />
                  )}
                </div>
                );
              })}
            </div>
          ) : (
            <div className="qc-empty">No documents currently</div>
          )}
    </Collapsible>
  );
}

// R-162 §10.1 — per-lane operator roster (monitor-gated). Mirrors QueueCard's
// roster; full doc/lock detail lives in each chip's title attr.
const MAX_VISIBLE_ROSTER = 3;

function OperatorRoster({ row }: { row: JobTypeQueueRow }) {
  const operators = row.operators ?? [];
  if (operators.length === 0) {
    return <div className="qc-jt-roster qc-jt-roster--empty">No operators</div>;
  }
  const visible = operators.slice(0, MAX_VISIBLE_ROSTER);
  const overflow = operators.length - visible.length;
  return (
    <div className="qc-jt-roster" aria-label="operators on this lane">
      {visible.map((op) => (
        <Link
          key={op.userId}
          to={`/admin/operators?tab=historical&operatorId=${op.userId}`}
          className="qc-jt-roster-op"
          title={operatorTitle(op)}
        >
          {op.displayName}
        </Link>
      ))}
      {overflow > 0 && (
        <span className="qc-jt-roster-overflow" title={`${overflow} more`}>
          +{overflow} more
        </span>
      )}
    </div>
  );
}

function operatorTitle(op: JobTypeOperator): string {
  const doc = op.currentDocumentId != null ? (op.currentDocDisplayId ?? op.currentDocumentId) : "idle";
  const since = op.lockedSince ? ` since ${timeAgo(op.lockedSince)}` : "";
  return `${op.displayName} — ${doc}${since}`;
}

function formatStepType(stepType: string): string {
  // F-039 — short acronym steps render fully uppercased ("QC", not "Qc").
  if (stepType.length <= 2) return stepType.toUpperCase();
  return stepType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
